import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { GlyphButton, logical } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { TipsContext } from "../bar/hover-words.tsx";
import { legendWords, type HudMinimap } from "../minimap/minimap.tsx";
import { FLOAT_STRIP, STRIP_BUTTON, floatLegend } from "./float.ts";
import { PANEL_INSET } from "./layout.ts";
import { legendStrip } from "./nearby.ts";
import type { MapPopout, PopoutFrame } from "./popout-host.ts";
import { keptInterfaceStep, subscribeInterfaceStep } from "../display-settings.ts";
import { deviceSize, popoutLayout, popoutProperties } from "./popout.ts";
import type { WorldMapSnapshot } from "./store.ts";
import { LevelView, MapControls, ZoomControls, useMapLevels, useZoomInput, type HudWorldMap, type WorldMapControl } from "./window.tsx";

/**
 * The pop-out (P2.12-T18, ADR-100 decisions 29 to 36 and 50): the Map drawn into a window of its own, a React portal
 * into the new window's document, so its store, its pictures and its marks are the page's own and nothing is opened for
 * it. Its UI scale and layout come from its size in device pixels alone (`popoutLayout`): at the small layout a header of
 * 16 pixel icon buttons (Places, Centre, Clear pin, the two zooms and Pop in) sits over the map and Known places is a sheet
 * over its lower half; at the roomier layouts the window's own controls lie over the map and the Places button and panel
 * are the window's. Its own keys are C and the zoom keys; Escape and M do nothing there, and the movement keys do not
 * reach the game, since a key goes to the focused window.
 */

/**
 * The size of a window in device pixels, kept current as it is resized.
 *
 * @param frame The pop-out's window.
 * @returns The size in device pixels and the window's device pixel ratio.
 */
function useDeviceSize(frame: PopoutFrame): { readonly device: { readonly width: number; readonly height: number }; readonly ratio: number } {
  const read = (): string => `${frame.innerWidth}x${frame.innerHeight}@${frame.devicePixelRatio}`;
  const [, setKey] = useState(read);
  useEffect(() => {
    const onResize = (): void => setKey(read());
    frame.addEventListener("resize", onResize);
    return () => frame.removeEventListener("resize", onResize);
  }, [frame]);
  return { device: deviceSize({ width: frame.innerWidth, height: frame.innerHeight }, frame.devicePixelRatio), ratio: frame.devicePixelRatio };
}

/**
 * What a key does in the pop-out: C centres at Region, `-` zooms out, `=` and `+` zoom in. A key typed in a field is the
 * field's. Escape and M, and every other key, do nothing.
 *
 * @param event The key press, heard on the pop-out's document.
 * @param store The store the key acts on.
 */
function popoutKey(event: KeyboardEvent, store: WorldMapControl): void {
  const target = event.target as { readonly tagName?: string; readonly isContentEditable?: boolean } | null;
  if (event.ctrlKey || event.altKey || event.metaKey || target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable === true) {
    return;
  }
  if (event.key === "c" || event.key === "C") {
    if (store.getSnapshot().level === "region") {
      store.centre();
    }
  } else if (event.key === "-") {
    store.zoomOut();
  } else if (event.key === "=" || event.key === "+") {
    store.zoomIn();
  }
}

/**
 * The small layout's header: 16 pixel icon buttons, each named for a screen reader. Places, Centre and Clear pin are
 * Region's.
 *
 * @param props The `store` and its `snapshot`.
 * @returns The header.
 */
function Header({ store, snapshot }: { readonly store: WorldMapControl; readonly snapshot: WorldMapSnapshot }): ReactElement {
  const region = snapshot.level === "region";
  return (
    <div className="ot-worldmap-popout__header" data-worldmap="popout-header" style={{ height: logical(FLOAT_STRIP), gap: logical(2), paddingLeft: logical(2) }}>
      {region ? <GlyphButton glyph="book" label={say("worldMap.places")} size={STRIP_BUTTON} keepFocus expanded={snapshot.panelOpen} onClick={() => store.togglePanel()} data={{ "data-worldmap": "popout-places" }} /> : null}
      {region ? <GlyphButton glyph="bullseye" label={say("worldMap.centre")} size={STRIP_BUTTON} keepFocus disabled={snapshot.following} onClick={() => store.centre()} data={{ "data-worldmap": "popout-centre" }} /> : null}
      {region ? <GlyphButton glyph="unlink" label={say("worldMap.clearPin")} size={STRIP_BUTTON} keepFocus disabled={snapshot.pin === null} onClick={() => store.clearPin()} data={{ "data-worldmap": "popout-clear-pin" }} /> : null}
      <GlyphButton glyph="minus" label={say("worldMap.zoomOut")} size={STRIP_BUTTON} keepFocus disabled={!snapshot.canZoomOut} onClick={() => store.zoomOut()} data={{ "data-worldmap": "popout-zoom-out" }} />
      <GlyphButton glyph="plus" label={say("worldMap.zoomIn")} size={STRIP_BUTTON} keepFocus disabled={!snapshot.canZoomIn} onClick={() => store.zoomIn()} data={{ "data-worldmap": "popout-zoom-in" }} />
      <GlyphButton glyph="popin" label={say("worldMap.popIn")} size={STRIP_BUTTON} keepFocus onClick={() => store.popIn()} data={{ "data-worldmap": "popout-pop-in" }} />
    </div>
  );
}

/**
 * What the pop-out draws inside its own document: the level's view at the pop-out's size, with the controls its layout
 * calls for.
 *
 * @param props The pop-out's `frame`, the `store` and its `snapshot`, and the `minimap` Nearby is drawn from, if any.
 * @returns The pop-out's content.
 */
function PopoutContent({ frame, store, snapshot, minimap }: { readonly frame: PopoutFrame; readonly store: WorldMapControl; readonly snapshot: WorldMapSnapshot; readonly minimap: HudMinimap | undefined }): ReactElement {
  const { device, ratio } = useDeviceSize(frame);
  const step = useSyncExternalStore(subscribeInterfaceStep, keptInterfaceStep);
  const { scale, logical: size, layout } = popoutLayout(device, step);
  const small = layout === "small";
  const head = small ? FLOAT_STRIP : 0;
  const view = { width: size.width, height: Math.max(0, size.height - head) };
  const root = useRef<HTMLDivElement>(null);
  const pinching = useRef(false);
  useLayoutEffect(() => {
    store.setView(view);
  }, [store, view.width, view.height]);
  useLayoutEffect(() => {
    frame.document.title = snapshot.title;
  }, [frame, snapshot.title]);
  useZoomInput(root, store, pinching);
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => popoutKey(event, store);
    frame.document.addEventListener("keydown", onKey);
    return () => frame.document.removeEventListener("keydown", onKey);
  }, [frame, store]);
  const wanted = legendStrip(view.width, legendWords());
  const strip = floatLegend(view, wanted) ? wanted : 0;
  const { shown, held } = useMapLevels(view, strip, snapshot, minimap);
  const controls = small ? null : (
    <>
      <ZoomControls map={store} snapshot={snapshot} />
      {shown === "region" ? <MapControls map={store} snapshot={snapshot} /> : null}
      <GlyphButton glyph="popin" label={say("worldMap.popIn")} size={16} keepFocus onClick={() => store.popIn()} data={{ "data-worldmap": "popout-pop-in" }} />
    </>
  );
  const properties = popoutProperties(device, ratio, step) as unknown as Record<string, string>;
  return (
    <div ref={root} className="ot-worldmap-popout" data-worldmap="popout" data-layout={layout} data-scale={String(scale)} data-level={snapshot.level} style={{ ...properties, width: logical(size.width), height: logical(size.height) }}>
      {small ? <Header store={store} snapshot={snapshot} /> : null}
      <LevelView shown={shown} held={held} view={view} strip={strip} store={store} snapshot={snapshot} minimap={minimap} controls={controls} corner={!small} panelTop={small ? PANEL_INSET : undefined} pinching={pinching} />
    </div>
  );
}

/**
 * Makes a new document ready to hold the map: no margin or scrollbars, the map's ground colour behind it.
 *
 * @param frame The pop-out's window.
 */
function prepare(frame: PopoutFrame): void {
  for (const element of [frame.document.documentElement, frame.document.body]) {
    element.style.margin = "0";
    element.style.overflow = "hidden";
    element.style.background = "var(--ot-ink-900)";
    element.style.height = "100%";
  }
}

/**
 * The pop-out, always mounted with the HUD: it draws the Map into the window the controller holds while the store says the
 * Map is open and popped out, brings the window forward when M or the Map button ask, shows the Map in the page again when
 * the window ends, and closes the window when the store no longer wants it (the Map shut, the page's character gone).
 *
 * @param props The `map` it reads and acts on (with its `popout` controller) and the `minimap` Nearby is drawn from.
 * @returns The portal, or nothing.
 */
export function WorldMapPopout({ map, minimap }: { readonly map: HudWorldMap; readonly minimap?: HudMinimap | undefined }): ReactElement | null {
  const store = map.store;
  const popout: MapPopout | undefined = map.popout;
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const subscribe = popout?.subscribe ?? noWatch;
  const frame = useSyncExternalStore(subscribe, popout?.getSnapshot ?? noFrame, noFrame);
  const wanted = snapshot.open && snapshot.presentation === "popped";
  useEffect(() => {
    if (popout === undefined) {
      return undefined;
    }
    const stopClosed = popout.onClosed(() => store.popIn());
    const stopForward = store.onBringForward(() => popout.focus());
    return () => {
      stopClosed();
      stopForward();
      popout.close();
    };
  }, [popout, store]);
  useEffect(() => {
    if (popout !== undefined && frame !== null && !wanted) {
      popout.close();
    }
  }, [popout, frame, wanted]);
  useLayoutEffect(() => {
    if (frame !== null) {
      prepare(frame);
    }
  }, [frame]);
  if (frame === null || !wanted || !snapshot.ready) {
    return null;
  }
  return createPortal(
    <TipsContext.Provider value={null}>
      <PopoutContent frame={frame} store={store} snapshot={snapshot} minimap={minimap} />
    </TipsContext.Provider>,
    frame.document.body,
  );
}

/**
 * A subscription that never fires, for a page with no pop-out.
 *
 * @returns A function that stops listening, which does nothing.
 */
function noWatch(): () => void {
  return () => undefined;
}

/**
 * No window, for a page with no pop-out.
 *
 * @returns Null.
 */
function noFrame(): PopoutFrame | null {
  return null;
}
